/**
 * DemoRequestPublicPage — Page publique dédiée à la demande de démo
 *
 * Cette page n'est PAS référencée dans la navigation de la landing page publique
 * (pour éviter que les utilisateurs internes de clients HR Engine ne la
 * remplissent par erreur). Son URL est volontairement obscure (`/demande-demo`)
 * et doit être partagée manuellement par l'équipe commerciale aux prospects.
 *
 * La landing page publique (/) n'expose que le formulaire de devis.
 */
import { Link } from 'react-router-dom';
import { Sparkles, ArrowLeft, Eye, Check, Shield, Activity, Zap } from 'lucide-react';
import { DemoRequest } from './LandingPagePremium';

export default function DemoRequestPublicPage() {
  return (
    <div className="min-h-screen bg-ink-50 font-sans" data-testid="demo-request-public-page">
      {/* Header minimal */}
      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-ink-200/80 shadow-[0_8px_24px_-12px_rgba(15,23,42,0.06)]">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex items-center gap-2.5" data-testid="demo-page-logo">
            <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-brand to-brand-hover shadow-[0_8px_24px_-10px_rgba(37,99,235,0.6)]">
              <Sparkles className="h-5 w-5 text-white" />
            </div>
            <div className="leading-none">
              <div className="text-base font-semibold tracking-tight text-ink-900">HR Engine</div>
            </div>
          </Link>
          <Link
            to="/"
            data-testid="demo-page-back-home"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3.5 py-2 text-sm font-medium text-ink-700 hover:border-brand/40 hover:bg-ink-50 transition"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Link>
        </div>
      </header>

      {/* Hero / Intro courte */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(37,99,235,0.08),transparent)]" />
        <div className="mx-auto max-w-4xl px-4 pb-8 pt-14 lg:px-8 lg:pt-20 text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand-soft/60 px-3 py-1.5 text-xs font-medium text-brand">
            <Eye className="h-3.5 w-3.5" />
            Démonstration personnalisée HR Engine
          </span>
          <h1 className="mt-6 text-3xl font-semibold leading-tight tracking-tight text-ink-900 sm:text-4xl lg:text-5xl">
            Découvrez HR Engine en{' '}
            <span className="bg-gradient-to-r from-brand to-ai bg-clip-text text-transparent">
              30 minutes
            </span>
            .
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-base leading-relaxed text-ink-600">
            Un expert HR Engine vous présente la plateforme en direct, adaptée à votre métier et vos enjeux RH.
            Sans engagement, réponse sous 24h ouvrées.
          </p>

          <div className="mt-7 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-xs text-ink-600">
            {[
              { icon: Zap, text: '30 min — démonstration ciblée' },
              { icon: Shield, text: 'Confidentialité garantie' },
              { icon: Activity, text: 'Support local 24/7' },
              { icon: Check, text: 'Sans engagement' },
            ].map(({ icon: Icon, text }) => (
              <div key={text} className="flex items-center gap-2">
                <div className="grid h-7 w-7 place-items-center rounded-lg bg-brand-soft text-brand">
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <span className="font-medium">{text}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Formulaire de demande de démo (mode verrouillé) */}
      <DemoRequest forcedMode="demo" />

      {/* Footer minimal */}
      <footer className="bg-[#0B1437] py-10 text-slate-300">
        <div className="mx-auto max-w-7xl px-4 lg:px-8">
          <div className="flex flex-col items-start justify-between gap-3 text-xs text-slate-400 sm:flex-row sm:items-center">
            <div className="flex items-center gap-2.5">
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600">
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <span className="text-sm font-semibold text-white">HR Engine</span>
              <span className="opacity-60">by Webtech</span>
            </div>
            <p>© {new Date().getFullYear()} HR Engine. Tous droits réservés.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
